import axios from 'axios';
import config from './config';

// Match pos-resturant / cayenne-builder-fe auth pattern:
// - rely on browser cookies (sid) when present
// - include credentials
axios.defaults.withCredentials = true;
axios.defaults.crossDomain = true;
axios.defaults.baseURL = config.apiBaseUrl;

const CSRF_HEADER = 'x-frappe-csrf-token';
const TOKEN_ENDPOINT = 'method/custom_api.api.getToken';

let csrfToken = null;
let csrfTokenRequest = null;

function isValidCsrfToken(token) {
  return token && token !== '{{ csrf_token }}';
}

function getInjectedCsrfToken() {
  if (typeof window === 'undefined') return null;

  // Frappe website/desk pages may inject either global shape.
  // eslint-disable-next-line no-underscore-dangle
  const token = window.csrf_token || window.frappe?.csrf_token;
  return isValidCsrfToken(token) ? token : null;
}

function setCsrfToken(token) {
  if (!isValidCsrfToken(token)) return null;

  csrfToken = token;
  axios.defaults.headers.common[CSRF_HEADER] = token;
  return token;
}

function clearCsrfToken() {
  csrfToken = null;
  delete axios.defaults.headers.common[CSRF_HEADER];
}

export async function fetchCsrfToken({ force = false } = {}) {
  if (!force) {
    const injectedToken = getInjectedCsrfToken();
    if (injectedToken) return setCsrfToken(injectedToken);
    if (csrfToken) return csrfToken;
    if (csrfTokenRequest) return csrfTokenRequest;
  }

  csrfTokenRequest = axios
    .get(TOKEN_ENDPOINT, {
      headers: {
        Accept: 'application/json',
      },
    })
    .then((res) => setCsrfToken(res?.data?.message))
    .finally(() => {
      csrfTokenRequest = null;
    });

  return csrfTokenRequest;
}

export function getHeader() {
  const headers = {
    'Access-Control-Allow-Credentials': true,
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
  };

  const token = csrfToken || getInjectedCsrfToken();
  if (token) {
    headers[CSRF_HEADER] = token;
  }

  return headers;
}

function isCsrfError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message = [data?.exc_type, data?.exception, data?.message, error?.message]
    .filter(Boolean)
    .join(' ');

  return status === 403 && /csrf|CSRFTokenError|Invalid Request/i.test(message);
}

export async function apiGet(url, params) {
  const res = await axios.get(url, { params, headers: getHeader() });
  return res.data;
}

export async function apiPost(url, data) {
  await fetchCsrfToken().catch(() => null);

  try {
    const res = await axios.post(url, data, { headers: getHeader() });
    return res.data;
  } catch (error) {
    if (!isCsrfError(error)) {
      throw error;
    }

    clearCsrfToken();
    await fetchCsrfToken({ force: true });
    const res = await axios.post(url, data, { headers: getHeader() });
    return res.data;
  }
}
